import { all, call, put, takeLatest } from 'redux-saga/effects';

import {
  getBuilding,
  getApp,
  getClaim,
  getPolicy,
  getBuildingDelegation,
  getBuildingInvoice,
  cancelPolicies,
  getPolicyCancelLogs,
  getPolicyDetail,
  getApplicationDetail,
  getClaimDetail,
  getBuildingDetail,
} from './APIs/property';
import types from '../constants/propertyConstant';
import {
  setBuildingAction,
  setAppAction,
  setClaimAction,
  setPolicyAction,
  getPolicyCancelAction,
  getPolicyAction,
  setPolicyCancelAction,
  setBuildingDelegationAction,
  setBuildingInvoiceAction,
  setPolicyCancelLogsAction,
  setPolicyCancelLogsErrorAction,
  setPolicyDetailAction,
  setAppDetailAction,
  setClaimDetailAction,
  setBuildingDetailAction,
  setAppDetailErrorAction,
  setPolicyDetailErrorAction,
  setClaimDetailErrorAction,
  setBuildingDetailErrorAction,
  setBuildingErrorAction,
  setAppErrorAction,
  setPolicyErrorAction,
  setClaimErrorAction,
} from '../actions/propertyActions';
import { successNotif, failedNotif } from '../actions/notifActions';
import { getApiErrorMessage } from 'utils/apiError';

function* getBuildingSaga({ payload }) {
  try {
    const response = yield getBuilding(payload);
    yield put(setBuildingAction(response));
  } catch (error) {
    const message = getApiErrorMessage(error, 'Unable to load properties.');
    yield put(setBuildingErrorAction(message));
    yield put(failedNotif(message));
  }
}

function* getAppSaga({ payload }) {
  try {
    const data = yield getApp(payload);
    yield put(setAppAction(data));
  } catch (error) {
    const message = getApiErrorMessage(error, 'Unable to load applications.');
    yield put(setAppErrorAction(message));
    yield put(failedNotif(message));
  }
}

function* getClaimSaga({ payload }) {
  try {
    const data = yield getClaim(payload);
    yield put(setClaimAction(data));
  } catch (error) {
    const message = getApiErrorMessage(error, 'Unable to load claims.');
    yield put(setClaimErrorAction(message));
    yield put(failedNotif(message));
  }
}

function* getPolicySaga({ payload }) {
  try {
    const data = yield getPolicy(payload);
    yield put(setPolicyAction(data));
  } catch (error) {
    const message = getApiErrorMessage(error, 'Unable to load policies.');
    yield put(setPolicyErrorAction(message));
    yield put(failedNotif(message));
  }
}

function* getPolicyCancelSaga({ payload }) {
  try {
    const data = yield getPolicy({ ...(payload || {}), cancel: true });
    yield put(setPolicyCancelAction(data));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load flagged cancellations.')));
  }
}

function* cancelPoliciesSaga({ payload }) {
  try {
    yield put(successNotif('loading'));
    const res = yield cancelPolicies(payload);
    yield put(successNotif(res));
  } catch (error) {
    yield put(
      failedNotif(getApiErrorMessage(error, 'Failed to cancel policies. Please try again.')),
    );
  }
}

function* onGetBuilding() {
  yield takeLatest(types.GET_BUILDING, getBuildingSaga);
}

function* onGetApp() {
  yield takeLatest(types.GET_APP, getAppSaga);
}

function* onGetClaim() {
  yield takeLatest(types.GET_CLAIM, getClaimSaga);
}

function* onGetPolicy() {
  yield takeLatest(types.GET_POLICY, getPolicySaga);
}

function* onGetPolicyCancel() {
  yield takeLatest(types.GET_POLICY_CANCEL, getPolicyCancelSaga);
}

function* onCancelPolicies() {
  yield takeLatest(types.CANCEL_POLICIES, cancelPoliciesSaga);
}

function* getBuildingDelegationSaga({ payload }) {
  try {
    const res = yield getBuildingDelegation(payload);
    yield put(setBuildingDelegationAction(res));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load building delegation.')));
  }
}

function* onGetBuildingDelegation() {
  yield takeLatest(types.GET_BUILDING_DELEGATION, getBuildingDelegationSaga);
}

function* getBuildingInvoiceSaga({ payload }) {
  try {
    const res = yield getBuildingInvoice(payload);
    yield put(setBuildingInvoiceAction(res));
  } catch (error) {
    yield put(setBuildingInvoiceAction({ invoiceData: [], schedule_date: null }));
    yield put(failedNotif(getApiErrorMessage(error, 'Unable to load building invoices.')));
  }
}

function* onGetBuildingInvoice() {
  yield takeLatest(types.GET_BUILDING_INVOICE, getBuildingInvoiceSaga);
}

function* getPolicyCancelLogsSaga({ payload }) {
  try {
    const data = yield getPolicyCancelLogs(payload);
    yield put(setPolicyCancelLogsAction(data));
  } catch (error) {
    const message = getApiErrorMessage(error, 'Unable to load policy cancellation logs.');
    yield put(setPolicyCancelLogsErrorAction(message));
    yield put(failedNotif(message));
  }
}

function* onGetPolicyCancelLogs() {
  yield takeLatest(types.GET_POLICY_CANCEL_LOGS, getPolicyCancelLogsSaga);
}

function* getPolicyDetailSaga({ payload }) {
  try {
    const data = yield getPolicyDetail(payload);
    yield put(setPolicyDetailAction(data));
  } catch (error) {
    yield put(setPolicyDetailErrorAction());
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to load policy details.')));
  }
}

function* onGetPolicyDetail() {
  yield takeLatest(types.GET_POLICY_DETAIL, getPolicyDetailSaga);
}

function* getAppDetailSaga({ payload }) {
  try {
    const data = yield getApplicationDetail(payload);
    yield put(setAppDetailAction(data));
  } catch (error) {
    yield put(setAppDetailErrorAction());
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to load application details.')));
  }
}

function* onGetAppDetail() {
  yield takeLatest(types.GET_APP_DETAIL, getAppDetailSaga);
}

function* getClaimDetailSaga({ payload }) {
  try {
    const data = yield getClaimDetail(payload);
    yield put(setClaimDetailAction(data));
  } catch (error) {
    yield put(setClaimDetailErrorAction());
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to load claim details.')));
  }
}

function* onGetClaimDetail() {
  yield takeLatest(types.GET_CLAIM_DETAIL, getClaimDetailSaga);
}

function* getBuildingDetailSaga({ payload }) {
  try {
    const data = yield getBuildingDetail(payload);
    yield put(setBuildingDetailAction(data));
  } catch (error) {
    yield put(setBuildingDetailErrorAction());
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to load property details.')));
  }
}

function* onGetBuildingDetail() {
  yield takeLatest(types.GET_BUILDING_DETAIL, getBuildingDetailSaga);
}

export default function* propertySagas() {
  yield all([
    call(onGetBuilding),
    call(onGetApp),
    call(onGetClaim),
    call(onGetPolicy),
    call(onGetPolicyCancel),
    call(onCancelPolicies),
    call(onGetBuildingDelegation),
    call(onGetBuildingInvoice),
    call(onGetPolicyCancelLogs),
    call(onGetPolicyDetail),
    call(onGetAppDetail),
    call(onGetClaimDetail),
    call(onGetBuildingDetail),
  ]);
}
